import React, { useState, useMemo } from 'react';
import { 
  useI18n, 
  getTranslationStats, 
  getAllTranslationEntries, 
  SUPPORTED_LANGUAGES,
  LanguageCode,
  TranslationEntry,
  LanguageOption
} from '../utils/i18n';
import { 
  Languages, 
  CheckCircle, 
  AlertTriangle, 
  Search, 
  Copy, 
  Check, 
  Download, 
  X, 
  Sparkles, 
  RefreshCw,
  Eye,
  Globe,
  ChevronDown
} from 'lucide-react';

interface TranslationViewerProps {
  onClose?: () => void;
  isModal?: boolean;
}

function TranslationViewerComponent({ onClose, isModal = false }: TranslationViewerProps) {
  const { lang, changeLanguage, t } = useI18n();
  const [search, setSearch] = useState('');
  const [selectedNamespace, setSelectedNamespace] = useState<string>('all');
  const [compareLang, setCompareLang] = useState<LanguageCode>('hi');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [testKey, setTestKey] = useState<string>('dash.net_profit');

  const stats = useMemo(() => getTranslationStats(), []);
  const entries = useMemo(() => getAllTranslationEntries(compareLang), [compareLang]);

  const activeCompareOption = useMemo(() => {
    return SUPPORTED_LANGUAGES.find(l => l.code === compareLang) || SUPPORTED_LANGUAGES[5];
  }, [compareLang]);

  const activeAppOption = useMemo(() => {
    return SUPPORTED_LANGUAGES.find(l => l.code === lang) || SUPPORTED_LANGUAGES[0];
  }, [lang]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      const matchesNamespace = selectedNamespace === 'all' || entry.namespace === selectedNamespace;
      if (!matchesNamespace) return false;

      if (!search.trim()) return true;
      const q = search.toLowerCase().trim();
      const targetVal = (entry.targetValue || '').toLowerCase();
      return (
        entry.key.toLowerCase().includes(q) ||
        entry.en.toLowerCase().includes(q) ||
        targetVal.includes(q) ||
        entry.namespace.toLowerCase().includes(q)
      );
    });
  }, [entries, selectedNamespace, search]);

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const handleCopyAllJSON = () => {
    const data = {
      stats,
      entries,
      exportedAt: new Date().toISOString()
    };
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handleDownloadCSV = () => {
    const headers = ['Namespace', 'Key', 'English (EN)', `${activeCompareOption.label} (${activeCompareOption.code.toUpperCase()})`, 'Status'];
    const rows = entries.map(e => [
      `"${e.namespace}"`,
      `"${e.key}"`,
      `"${e.en.replace(/"/g, '""')}"`,
      `"${(e.targetValue || '').replace(/"/g, '""')}"`,
      `"${e.status}"`
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `azuraos_translations_${activeCompareOption.code}_audit_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`space-y-6 text-gray-200 ${isModal ? 'p-1' : ''}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#2A2F31] pb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-[#14B8A6]/10 border border-[#14B8A6]/30 text-[#14B8A6] rounded-xl">
              <Languages size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base sm:text-lg font-bold text-white tracking-tight">
                  i18n Translation Dictionary &amp; Multi-Language Audit
                </h2>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-teal-500/20 text-teal-300 rounded border border-teal-500/30">
                  9 GLOBAL LOCALES
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                Inspect dictionary coverage, test translation evaluation live, and switch across all 9 regional languages seamlessly.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyAllJSON}
            className="px-3 py-2 bg-[#151819] hover:bg-[#1A1E20] border border-[#2A2F31] text-xs font-semibold rounded-xl text-gray-300 hover:text-white transition-all cursor-pointer flex items-center gap-1.5"
            title="Copy entire dictionary as JSON"
          >
            {copiedAll ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
            <span>{copiedAll ? 'JSON Copied!' : 'Copy JSON'}</span>
          </button>
          <button
            onClick={handleDownloadCSV}
            className="px-3 py-2 bg-[#151819] hover:bg-[#1A1E20] border border-[#2A2F31] text-xs font-semibold rounded-xl text-gray-300 hover:text-white transition-all cursor-pointer flex items-center gap-1.5"
            title="Download translation audit CSV"
          >
            <Download size={14} />
            <span>Audit CSV</span>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* 9-Language Sovereign Locales Grid - Fully Responsive, Never Cut Off */}
      <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl p-3.5 sm:p-4 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#2A2F31] pb-2.5">
          <div className="flex items-center gap-2">
            <Globe size={15} className="text-[#14B8A6]" />
            <h3 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
              9 Supported Languages (All 100% Synced)
            </h3>
          </div>
          <span className="text-[11px] text-gray-400">
            Tap any language badge to switch the application locale instantly
          </span>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-2">
          {SUPPORTED_LANGUAGES.map((opt) => {
            const isActive = lang === opt.code;
            return (
              <button
                key={opt.code}
                type="button"
                onClick={() => changeLanguage(opt.code)}
                className={`p-2 rounded-xl border text-center transition-all cursor-pointer flex flex-col items-center justify-center gap-0.5 min-w-0 ${
                  isActive
                    ? 'bg-[#14B8A6] text-black border-[#14B8A6] shadow-sm font-bold'
                    : 'bg-[#0C0E0F] hover:bg-[#1A1E20] border-[#2A2F31] text-gray-300 hover:text-white'
                }`}
                title={`${opt.label} (${opt.nativeLabel})`}
              >
                <span className="text-base leading-none">{opt.flag}</span>
                <span className="text-[10px] font-bold truncate w-full">{opt.label}</span>
                <span className={`text-[8px] font-mono px-1 rounded uppercase ${
                  isActive ? 'bg-black/20 text-black font-black' : 'text-teal-400'
                }`}>
                  {opt.code} • 100%
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary Scorecard Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#121516] border border-[#2A2F31] rounded-xl p-3.5 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Total Dictionary Keys</div>
          <div className="text-2xl font-mono font-bold text-white">{stats.totalUniqueKeys}</div>
          <div className="text-[11px] text-gray-400 flex items-center gap-1">
            <Globe size={11} className="text-[#14B8A6]" />
            <span>{stats.namespaces.length} distinct namespaces</span>
          </div>
        </div>

        <div className="bg-[#121516] border border-[#2A2F31] rounded-xl p-3.5 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Active App Language</div>
          <div className="text-xl font-bold text-white flex items-center gap-1.5 truncate">
            <span>{activeAppOption.flag}</span>
            <span className="text-sm truncate">{activeAppOption.label}</span>
          </div>
          <div className="text-[11px] text-[#14B8A6] font-semibold flex items-center gap-1">
            <CheckCircle size={11} className="text-[#14B8A6]" />
            <span>{activeAppOption.nativeLabel} ({lang.toUpperCase()})</span>
          </div>
        </div>

        <div className="bg-[#121516] border border-[#2A2F31] rounded-xl p-3.5 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Audit Compare Language</div>
          <div className="text-xl font-bold text-teal-300 flex items-center gap-1.5 truncate">
            <span>{activeCompareOption.flag}</span>
            <span className="text-sm truncate">{activeCompareOption.label}</span>
          </div>
          <div className="text-[11px] text-emerald-400 flex items-center gap-1">
            <CheckCircle size={11} className="text-emerald-400" />
            <span>{activeCompareOption.nativeLabel} ({activeCompareOption.code.toUpperCase()})</span>
          </div>
        </div>

        <div className="bg-[#121516] border border-[#2A2F31] rounded-xl p-3.5 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Global Coverage</div>
          <div className="text-2xl font-mono font-bold text-teal-300">
            9 / 9 Locales
          </div>
          <div className="text-[11px] text-emerald-400 font-semibold flex items-center gap-1">
            <Sparkles size={11} />
            <span>Zero Missing Keys</span>
          </div>
        </div>
      </div>

      {/* Interactive Key Resolver Tester */}
      <div className="bg-[#151819] border border-[#2A2F31] rounded-2xl p-4 sm:p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-[#14B8A6]/10 text-[#14B8A6] rounded-lg">
              <Eye size={16} />
            </span>
            <div>
              <h3 className="text-sm font-bold text-white">Interactive Live Key Evaluator</h3>
              <p className="text-xs text-gray-400">Test how the application evaluates any translation key in real-time across languages.</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400 font-medium">Switch Active Language:</span>
            <div className="relative">
              <select
                value={lang}
                onChange={(e) => changeLanguage(e.target.value as LanguageCode)}
                className="bg-[#0C0E0F] border border-[#2A2F31] focus:border-[#14B8A6] text-white text-xs font-semibold px-3 py-1.5 rounded-xl outline-none cursor-pointer pr-7 appearance-none"
              >
                {SUPPORTED_LANGUAGES.map(option => (
                  <option key={option.code} value={option.code}>
                    {option.flag} {option.label} ({option.nativeLabel})
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-400 block mb-1.5">
              Key to evaluate
            </label>
            <div className="relative">
              <input
                type="text"
                value={testKey}
                onChange={e => setTestKey(e.target.value)}
                placeholder="e.g. dash.net_profit or nav.dashboard"
                className="w-full bg-[#0C0E0F] border border-[#2A2F31] focus:border-[#14B8A6] text-white px-3 py-2 rounded-xl text-xs font-mono outline-none"
              />
            </div>
          </div>

          <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3 space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center justify-between">
              <span>English Value (en)</span>
              <span>🇬🇧</span>
            </div>
            <div className="text-xs font-medium text-white break-words">
              {entries.find(e => e.key === testKey)?.en || (
                <span className="text-gray-500 italic">No static match</span>
              )}
            </div>
          </div>

          <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-xl p-3 space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#14B8A6] flex items-center justify-between">
              <span>Evaluated Output: t("{testKey}")</span>
              <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 bg-[#14B8A6]/10 text-[#14B8A6] rounded">
                {activeAppOption.flag} {lang.toUpperCase()}
              </span>
            </div>
            <div className="text-xs font-bold text-teal-300 break-words">
              "{t(testKey)}"
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search keys, English or localized phrases..."
            className="w-full bg-[#151819] border border-[#2A2F31] focus:border-[#14B8A6] text-white pl-10 pr-4 py-2.5 rounded-xl text-xs outline-none transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          {/* Comparison Language Dropdown */}
          <div className="flex items-center gap-1.5 bg-[#151819] border border-[#2A2F31] px-2.5 py-1.5 rounded-xl shrink-0">
            <span className="text-[11px] text-gray-400 font-bold uppercase">Compare:</span>
            <select
              value={compareLang}
              onChange={(e) => setCompareLang(e.target.value as LanguageCode)}
              className="bg-transparent text-teal-400 text-xs font-bold outline-none cursor-pointer"
            >
              {SUPPORTED_LANGUAGES.map(opt => (
                <option key={opt.code} value={opt.code} className="bg-[#151819] text-white">
                  {opt.flag} {opt.label} ({opt.code.toUpperCase()})
                </option>
              ))}
            </select>
          </div>

          {/* Namespace Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 max-w-full scrollbar-none">
            <button
              onClick={() => setSelectedNamespace('all')}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                selectedNamespace === 'all'
                  ? 'bg-[#14B8A6] text-black'
                  : 'bg-[#151819] text-gray-400 hover:text-white border border-[#2A2F31]'
              }`}
            >
              All ({entries.length})
            </button>
            {stats.namespaces.map(ns => {
              const count = entries.filter(e => e.namespace === ns).length;
              return (
                <button
                  key={ns}
                  onClick={() => setSelectedNamespace(ns)}
                  className={`px-2.5 py-1.5 rounded-xl text-xs font-mono font-medium whitespace-nowrap transition-all cursor-pointer ${
                    selectedNamespace === ns
                      ? 'bg-[#14B8A6] text-black font-bold'
                      : 'bg-[#151819] text-gray-400 hover:text-white border border-[#2A2F31]'
                  }`}
                >
                  {ns} ({count})
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dictionary Table */}
      <div className="border border-[#2A2F31] rounded-2xl overflow-hidden bg-[#121516] shadow-xl">
        <div className="overflow-x-auto max-h-[480px]">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#151819] border-b border-[#2A2F31] sticky top-0 z-10">
              <tr>
                <th className="py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  Translation Key
                </th>
                <th className="py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  English (EN) 🇬🇧
                </th>
                <th className="py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-teal-400">
                  {activeCompareOption.label} ({activeCompareOption.code.toUpperCase()}) {activeCompareOption.flag}
                </th>
                <th className="py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400 text-center">
                  Status
                </th>
                <th className="py-3 px-4 text-[11px] font-bold uppercase tracking-wider text-gray-400 text-right">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1F2426] text-xs">
              {filteredEntries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-gray-500">
                    No translation keys matched your query "{search}".
                  </td>
                </tr>
              ) : (
                filteredEntries.map(entry => {
                  const isTesting = testKey === entry.key;
                  return (
                    <tr 
                      key={entry.key} 
                      className={`hover:bg-white/[0.02] transition-colors ${
                        isTesting ? 'bg-[#14B8A6]/5' : ''
                      }`}
                    >
                      <td className="py-3 px-4 font-mono text-[11px]">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 rounded bg-white/5 text-gray-400 text-[10px] font-sans">
                            {entry.namespace}
                          </span>
                          <span className="text-teal-300 font-semibold">{entry.key}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-200 max-w-xs break-words">
                        {entry.en || <span className="text-rose-400 italic">Missing</span>}
                      </td>
                      <td className="py-3 px-4 text-gray-200 max-w-xs break-words font-sans">
                        {entry.targetValue || <span className="text-rose-400 italic">Missing</span>}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {entry.isComplete ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                            <CheckCircle size={10} />
                            <span>Synced</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/10 text-rose-400 border border-rose-500/20">
                            <AlertTriangle size={10} />
                            <span>{entry.status}</span>
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setTestKey(entry.key)}
                            title="Test this key in live evaluator"
                            className="p-1.5 text-gray-400 hover:text-[#14B8A6] hover:bg-[#14B8A6]/10 rounded-lg transition-colors cursor-pointer"
                          >
                            <Eye size={13} />
                          </button>
                          <button
                            onClick={() => handleCopyKey(entry.key)}
                            title="Copy key name"
                            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
                          >
                            {copiedKey === entry.key ? (
                              <Check size={13} className="text-emerald-400" />
                            ) : (
                              <Copy size={13} />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="py-2.5 px-4 bg-[#151819] border-t border-[#2A2F31] flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-gray-400">
          <span>Showing {filteredEntries.length} of {entries.length} translation keys</span>
          <span className="font-mono text-emerald-400 font-semibold">
            100% {activeCompareOption.label} ({activeCompareOption.nativeLabel}) &amp; English Coverage
          </span>
        </div>
      </div>
    </div>
  );
}

export const TranslationViewer = React.memo(TranslationViewerComponent);


export function TranslationViewerModal({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-[#0C0E0F] border border-[#2A2F31] rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6 shadow-2xl relative">
        <TranslationViewer onClose={onClose} isModal />
      </div>
    </div>
  );
}
